import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  sendOtp,
  verifyOtp,
  loginUser,
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp,
  resetPassword as resetPasswordService,
} from "../../Services/AuthService";
import { getUserById } from "../../Services/UserService";
import UserDropdown from "./UserDropdown";
import {
  faBars,
  faRefresh,
  faSignIn,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../../Assets/Logos/WFSLogo.png";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../Services/Api";

const navItems = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Blog", path: "/blog" },
  { label: "About Us", path: "/about" },
  { label: "Contact", path: "/contact" },
];

const getResolvedProfileImageUrl = (profileImageUrl) => {
  if (!profileImageUrl) {
    return "";
  }

  try {
    return new URL(profileImageUrl, api.defaults.baseURL).toString();
  } catch (error) {
    return profileImageUrl;
  }
};

function Nav() {
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [activeItem, setActiveItem] = useState("Home");
  const [underlineStyle, setUnderlineStyle] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(""); 
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [user, setUser] = useState(null);
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const profileContainerRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phoneNumber: "",
    address: "",
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loginData, setLoginData] = useState({
    userId: "",
    password: "",
  });
  const [loginError, setLoginError] = useState({});

  useEffect(() => {
    const syncUserFromStorage = async () => {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) {
        setUser(null);
        setProfileImageFailed(false);
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setProfileImageFailed(false);

        if (!parsedUser.userId) {
          return;
        }

        const response = await getUserById(parsedUser.userId);
        const latestUser = {
          ...parsedUser,
          userId: response.data.userId ?? parsedUser.userId,
          name: response.data.userName ?? parsedUser.name,
          email: response.data.email ?? parsedUser.email,
          profileImageUrl: response.data.profileImageUrl ?? null,
        };

        setUser(latestUser);
        localStorage.setItem("user", JSON.stringify(latestUser));
      } catch (error) {
        localStorage.removeItem("user");
        setUser(null);
        setProfileImageFailed(false);
      }
    };

    syncUserFromStorage();
    window.addEventListener("user-updated", syncUserFromStorage);

    return () => {
      window.removeEventListener("user-updated", syncUserFromStorage);
    };
  }, []);

  useEffect(() => {
    setProfileImageFailed(false);
  }, [user?.profileImageUrl]);

  useEffect(() => {
    const currentItem = navItems.find((item) => item.path === location.pathname);
    setActiveItem(currentItem?.label ?? "");
    setMobileMenuOpen(false);
    setShowProfile(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showProfile &&
        profileContainerRef.current &&
        !profileContainerRef.current.contains(event.target)
      ) {
        setShowProfile(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowProfile(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showProfile]);

  const moveUnderline = (target) => {
    if (!target || !navRef.current || window.innerWidth < 1024) {
      return;
    }

    const linkRect = target.getBoundingClientRect();
    const navRect = navRef.current.getBoundingClientRect();

    setUnderlineStyle({
      left: linkRect.left - navRect.left,
      width: linkRect.width,
      opacity: 1,
    });
  };

  const handleMouseLeave = () => {
    if (!navRef.current) {
      return;
    }

    const activeLink = [...navRef.current.querySelectorAll("a")].find(
      (link) => link.textContent === activeItem
    );
    moveUnderline(activeLink);
  };

  const handleDesktopClick = (event, item) => {
    event.preventDefault();
    setActiveItem(item.label);
    moveUnderline(event.currentTarget);
    navigate(item.path);
  };

  useLayoutEffect(() => {
    const syncUnderline = () => {
      if (!navRef.current || window.innerWidth < 1024) {
        setUnderlineStyle((prev) => ({ ...prev, opacity: 0 }));
        return;
      }

      const activeLink = [...navRef.current.querySelectorAll("a")].find(
        (link) => link.textContent === activeItem
      );

      if (activeLink) {
        moveUnderline(activeLink);
      }
    };

    requestAnimationFrame(syncUnderline);
  }, [activeItem]);

  useEffect(() => {
    const handleResize = () => {
      if (!navRef.current) {
        return;
      }

      if (window.innerWidth < 1024) {
        setUnderlineStyle((prev) => ({ ...prev, opacity: 0 }));
        return;
      }

      const activeLink = [...navRef.current.querySelectorAll("a")].find(
        (link) => link.textContent === activeItem
      );

      if (activeLink) {
        moveUnderline(activeLink);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeItem]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevFormData) => {
      const updatedData = {
        ...prevFormData,
        [name]: value,
      };
      localStorage.setItem("registrationFormData", JSON.stringify(updatedData));
      return updatedData;
    });
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage("");

    try {
      const formattedData = {
        ...formData,
        phoneNumber: `+91${formData.phoneNumber}`,
      };

      await sendOtp(formattedData);

      setSuccessMessage("OTP sent to your email. Please verify to complete registration.");
      setOtpStep(true);
      // setShowOtpModal(true);

    } catch (err) {
      setErrors({
        error: err.response?.data?.error || "Something went wrong",
      });
    }
  };

   const handleVerifyOtp = async () => {
    try {
      const res = await verifyOtp({
        phoneNumber: `+91${formData.phoneNumber}`,
        email: formData.email,
        otp: otp,
      });

      await Swal.fire({
        icon: "success",
        title: "Registration Successful",
        text: `Your User ID is ${res.data.userId}`,
      });

      setShowOtpModal(false);
      setShowRegister(false);
      setShowLogin(true);
      localStorage.removeItem("registrationFormData");
      setFormData({
        name: "",
        email: "",
        password: "",
        phoneNumber: "",
        address: "",
      });
      setOtp("");
      setOtpStep(false);
      setErrors({});
      setSuccessMessage("");

    } catch (err) {
      setErrors({
        error: err.response?.data?.error || "Invalid OTP",
      });
    }
  };

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setSuccessMessage("");
    setLoginError({});

    try {
      const response = await loginUser(loginData);
      const loggedInUser = {
        userId: response.data.userId,
        name: response.data.name,
        email: response.data.email,
        profileImageUrl: response.data.profileImageUrl ?? null,
      };

      setUser(loggedInUser);
      localStorage.setItem("user", JSON.stringify(loggedInUser));
      window.dispatchEvent(new Event("user-updated"));

      await Swal.fire({
        icon: "success",
        title: "Login Successful",
        text: "Welcome back!",
        timer: 1500,
        showConfirmButton: false,
      });

      setSuccessMessage(
        "Login successful! Your User ID is: " + response.data.userId
      );
      setShowLogin(false);
      clearAuthStorage();
    } catch (error) {
      setLoginError({
        error:
          error.response?.data?.message || "Login failed. Please try again.",
      });
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error.response?.data?.message || "Invalid User ID or Password",
        confirmButtonColor: "#ef4444",
      });
      setErrors({
        error:
          error.response?.data?.message || "Login failed. Please try again.",
      });
    }
  };

  const clearAuthStorage = () => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
  };

  const resetAuthState = () => {
    setLoginData({ userId: "", password: "" });
    setFormData({
      name: "",
      email: "",
      password: "",
      phoneNumber: "",
      address: "",
    });
    setOtp("");
    setOtpStep(false);
    setShowOtpModal(false);
    setErrors({});
    setSuccessMessage("");
    localStorage.removeItem("registrationFormData");
  };

  const getInitials = (name) => {
    if (!name) return "";

    return name
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0].toUpperCase())
      .slice(0, 2) // only first 2 letters
      .join("");
  };

  const handleLogout = () => {
    setUser(null);
    setShowProfile(false);
    localStorage.removeItem("user");
    clearAuthStorage();
    resetAuthState();
    window.dispatchEvent(new Event("user-updated"));
    Swal.fire({
      icon: "success",
      title: "Logged Out",
      timer: 1200,
      showConfirmButton: false,
    });
  };

  const handleAccountSettings = () => {
    setShowProfile(false);
    navigate("/account-settings");
  };

  const [showForgot, setShowForgot] = useState(false);
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=reset
  const [forgotData, setForgotData] = useState({
    userId: "",
    otp: "",
    newPassword: "",
  });

  const handleForgotSendOtp = async () => {
    try {
      await sendForgotPasswordOtp({ Identifier: forgotData.userId });
      setStep(2);
      setErrors({});
    } catch (err) {
      setErrors({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to send OTP",
      });
    }
  };

  const handleForgotVerifyOtp = async () => {
    try {
      const res = await verifyForgotPasswordOtp({
        Identifier: forgotData.userId,
        otp: forgotData.otp,
      });

      if (res.status >= 200 && res.status < 300) {
        setStep(3);
        Swal.fire({
          icon: "success",
          title: "OTP verified successfully",
          timer: 1400,
          showConfirmButton: false,
        });
      }

      setErrors({});
    } catch (err) {
      setErrors({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Invalid OTP",
      });
    }
  };

  const handleForgotResetPassword = async () => {
    try {
      await resetPasswordService({
        Identifier: forgotData.userId,
        newPassword: forgotData.newPassword,
      });

      Swal.fire({
        icon: "success",
        title: "Password updated!",
        timer: 1500,
        showConfirmButton: false,
      });

      setShowForgot(false);
      setShowLogin(true);
      setStep(1);
      setForgotData({ userId: "", otp: "", newPassword: "" });
      setErrors({});
    } catch (err) {
      setErrors({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Password reset failed",
      });
    }
  };

  return (
    <>
      <nav className="relative z-10 bg-primary-default text-white shadow-xl">
        <div className="mx-auto flex min-h-[80px] max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src={logo}
              alt="WFS Logo"
              className="h-10 w-auto transition duration-300 hover:scale-110 sm:h-12"
            />
            <div className="min-w-0 leading-snug">
              <p className="truncate text-lg font-extrabold tracking-wider text-yellow-400 sm:text-xl">
                WealthGrow
              </p>
              <p className="truncate text-xs font-light text-blue-300 sm:text-sm">
                FinTax Services
              </p>
            </div>
            <FontAwesomeIcon
              icon={faRefresh}
              className="ml-2 hidden h-4 w-4 cursor-pointer text-blue-300 transition duration-300 hover:text-yellow-400 lg:block"
              title="Refresh"
              onClick={() => window.location.reload()}
            />
          </div>

          <div
            ref={navRef}
            className="relative hidden flex-1 items-center justify-center gap-6 text-sm font-medium lg:flex xl:gap-10"
            onMouseLeave={handleMouseLeave}
          >
            <span
              className="absolute bottom-[10px] h-[3px] rounded-sm bg-yellow-400 shadow-[0_0_12px_rgba(255,221,0,0.7)] transition-all duration-300 ease-in-out"
              style={underlineStyle}
            />

            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.path}
                onClick={(event) => handleDesktopClick(event, item)}
                onMouseEnter={(event) => moveUnderline(event.currentTarget)}
                className={`py-6 uppercase tracking-wider transition duration-300 ${
                  activeItem === item.label
                    ? "text-yellow-400"
                    : "text-white hover:text-yellow-400"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-3">
            {!user ? (
              <>
                <button
                  className="hidden items-center gap-2 rounded-full bg-accent-gold px-5 py-2 text-sm font-bold text-blue-900 shadow-lg transition duration-300 hover:scale-105 hover:bg-accent-goldLight sm:flex"
                  onClick={() => {
                    resetAuthState();
                    setShowLogin(true);
                  }}
                >
                  <FontAwesomeIcon icon={faSignIn} className="h-4 w-4" />
                  <span>Client Login</span>
                </button>
                <button
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/15 sm:hidden"
                  onClick={() => {
                    resetAuthState();
                    setShowLogin(true);
                  }}
                  aria-label="Client login"
                >
                  <FontAwesomeIcon icon={faSignIn} className="h-4 w-4" />
                </button>
              </>
            ) : (
              <div ref={profileContainerRef} className="relative overflow-visible z-[999]">
                <button
                  onClick={() => setShowProfile((prev) => !prev)}
                  className="flex rounded-full bg-accent-gold px-3 py-2 font-bold text-primary-dark shadow-lg transition hover:scale-105"
                  aria-expanded={showProfile}
                  aria-haspopup="menu"
                >
                  {user.profileImageUrl && !profileImageFailed ? (
                    <img
                      src={getResolvedProfileImageUrl(user.profileImageUrl)}
                      alt={user.name ?? "Profile"}
                      className="h-9 w-9 rounded-full border border-primary-dark object-cover"
                      onError={() => setProfileImageFailed(true)}
                    />
                  ) : (
                    <div className="rounded-full border border-primary-dark px-2 py-1">
                      {getInitials(user.name)}
                    </div>
                  )}
                  <div className="px-1 py-1">{user.name?.split(" ")[0]}</div>
                </button>

                {showProfile && (
                  <UserDropdown
                    user={user}
                    onLogout={handleLogout}
                    onAccountSettings={handleAccountSettings}
                  />
                )}
              </div>
            )}

            <button
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/15 lg:hidden"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <FontAwesomeIcon
                icon={mobileMenuOpen ? faTimes : faBars}
                className="h-4 w-4"
              />
            </button>
          </div>
        </div>

        <div
          className={`overflow-hidden border-t border-white/10 bg-primary-dark/95 px-4 transition-all duration-300 lg:hidden ${
            mobileMenuOpen ? "max-h-[420px] py-4" : "max-h-0 py-0"
          }`}
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-2">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={`rounded-2xl px-4 py-3 text-left text-sm font-medium tracking-wide transition ${
                  activeItem === item.label
                    ? "bg-accent-gold text-primary-dark"
                    : "bg-white/5 text-white hover:bg-white/10"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {showLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
          <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-neutral-border bg-white p-6 shadow-2xl sm:p-8">
            <button
              onClick={() => {
                setShowLogin(false);
                resetAuthState();
              }}
              className="absolute right-4 top-4 text-gray-500 hover:text-primary-dark"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>

            <h2 className="mb-6 text-center text-2xl font-bold text-primary-dark">
              Client Login
            </h2>

            <form className="space-y-4" onSubmit={handleLoginSubmit}>
              {loginError.error && (
                <p className="text-sm text-red-500">{loginError.error}</p>
              )}
              <div>
                <label className="font-medium text-primary-dark">User ID</label>
                <input
                  type="text"
                  name="userId"
                  value={loginData.userId}
                  onChange={handleLoginChange}
                  className="mt-1 w-full rounded-lg border border-neutral-border px-4 py-2 outline-none focus:ring-2 focus:ring-primary-light"
                  placeholder="Enter your User ID"
                />
              </div>

              <div>
                <label className="font-medium text-primary-dark">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  className="mt-1 w-full rounded-lg border border-neutral-border px-4 py-2 outline-none focus:ring-2 focus:ring-primary-light"
                  placeholder="Enter your Password"
                />
              </div>

              <div className="text-center">
                <button type="button"
                  onClick={() => {
                    setShowLogin(false);
                    setShowForgot(true);
                  }} 
                  className="font-medium text-primary-dark hover:underline"> Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                className="w-full rounded-lg bg-accent-gold py-2 font-bold text-primary-dark shadow-md transition-all duration-300 hover:scale-[1.03] hover:bg-accent-goldLight hover:shadow-xl active:scale-[0.97]"
              >
                Login
              </button>
            </form>

            <div className="mt-2 text-center">
              <p className="text-neutral-textSecondary">
                Don&apos;t have User ID?
                <button
                  className="ml-1 font-semibold text-primary-dark hover:underline"
                  onClick={() => {
                    resetAuthState();
                    setShowLogin(false);
                    setShowRegister(true);
                  }}
                >
                  Register Here
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {showRegister && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
          <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-neutral-border bg-white p-6 shadow-2xl animate-fadeIn sm:p-8">
            <button
              onClick={() => {
                setShowRegister(false);
                resetAuthState();
              }}
              className="absolute right-4 top-4 text-gray-500 hover:text-primary-dark"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>

            <h2 className="mb-6 text-center text-2xl font-bold text-primary-dark">
              Create an Account
            </h2>

            {errors.error && <p className="mb-3 text-sm text-red-500">{errors.error}</p>}
            {successMessage && (
              <p className="mb-3 text-sm text-green-600">{successMessage}</p>
            )}



            <form className="space-y-2" onSubmit={otpStep ? (e) => e.preventDefault() : handleSubmit}>

  {!otpStep && (
    <>
      <div>
        <label className="font-medium text-primary-dark">Full Name</label>
        <input
          type="text"
          name="name"
          className="mt-1 w-full rounded-lg border border-neutral-border px-4 py-2"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label className="font-medium text-primary-dark">Phone Number</label>
        <input
          type="tel"
          name="phoneNumber"
          className="w-full rounded-lg border px-4 py-2"
          value={formData.phoneNumber}
          onChange={handleChange}
          required
          // disabled={otpStep} // 🔥 lock after OTP sent
        />
      </div>

      <div>
        <label className="font-medium text-primary-dark">Address</label>
        <input
          type="text"
          name="address"
          className="w-full rounded-lg border px-4 py-2"
          value={formData.address}
          onChange={handleChange}
          required
        />
      </div>
    </>
  )}

  {/* ALWAYS SHOW email */}
  <div>
    <label className="font-medium text-primary-dark">Email</label>
    <input
      type="email"
      name="email"
      className="w-full rounded-lg border px-4 py-2"
      value={formData.email}
      onChange={handleChange}
      required
      disabled={otpStep} 
    />
  </div>

  {!otpStep && (
    <>
      <div>
        <label className="font-medium text-primary-dark">Password</label>
        <input
          type="password"
          name="password"
          className="w-full rounded-lg border px-4 py-2"
          value={formData.password}
          onChange={handleChange}
          required
        />
      </div>

      <div>
        <label className="font-medium text-primary-dark">Confirm Password</label>
        <input
          type="password"
          className="w-full rounded-lg border px-4 py-2"
          required
        />
      </div>
    </>
  )}

  {/* OTP FIELD */}
  {otpStep && (
    <div>
      <label className="font-medium text-primary-dark">Enter OTP</label>
      <input
        type="text"
        className="w-full rounded-lg border px-4 py-2"
        value={otp}
        onChange={(e) => setOtp(e.target.value)}
      />
    </div>
  )}

  {/* BUTTON */}
  {!otpStep ? (
    <button
      type="submit"
      className="w-full rounded-lg bg-accent-gold py-2 font-bold text-primary-dark"
    >
      Send OTP
    </button>
  ) : (
    <button
      type="button"
      onClick={handleVerifyOtp}
      className="w-full rounded-lg bg-accent-gold py-2 font-bold text-primary-dark"
    >
      Verify OTP
    </button>
  )}
</form>

            <div className="mt-2 text-center">
              <p className="text-neutral-textSecondary">
                Already have an account?
                <button
                  onClick={() => {
                    resetAuthState();
                    setShowRegister(false);
                    setShowLogin(true);
                  }}
                  className="ml-1 font-semibold text-primary-dark hover:underline"
                >
                  Login
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {showForgot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">

            <h2 className="text-xl font-bold text-center mb-4 text-primary-dark">
              Reset Password
            </h2>

            {step === 1 && (
              <>
                <input
                  type="text"
                  placeholder="Enter User ID / Mobile Number "
                  className="w-full border p-2 rounded-lg mb-4"
                  value={forgotData.userId}
                  onChange={(e) =>
                    setForgotData({ ...forgotData, userId: e.target.value })
                  }
                />

                <button
                  onClick={handleForgotSendOtp}
                  className="w-full bg-primary-default text-white py-2 rounded-lg"
                >
                  Send OTP
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  className="w-full border p-2 rounded-lg mb-4"
                  value={forgotData.otp}
                  onChange={(e) =>
                    setForgotData({ ...forgotData, otp: e.target.value })
                  }
                />

                <button
                  onClick={handleForgotVerifyOtp}
                  className="w-full bg-primary-default text-white py-2 rounded-lg"
                >
                  Verify OTP
                </button>
              </>
            )}

            {step === 3 && (
              <>
                <input
                  type="password"
                  placeholder="New Password"
                  className="w-full border p-2 rounded-lg mb-4"
                  value={forgotData.newPassword}
                  onChange={(e) =>
                    setForgotData({ ...forgotData, newPassword: e.target.value })
                  }
                />

                <button
                  onClick={handleForgotResetPassword}
                  className="w-full bg-accent-gold text-primary-dark py-2 rounded-lg"
                >
                  Reset Password
                </button>
              </>
            )}

            <button
              onClick={() => setShowForgot(false)}
              className="mt-4 text-sm text-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </>
  );
}

export default Nav;
